import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger, useSidebar,
} from "@/components/ui/sidebar";
import { NavLink } from "@/components/NavLink";
import {
  LayoutDashboard, User, Megaphone, FileText, Send, DollarSign, Wallet, Settings,
  Users, Building2, Flag, Bell, LogOut, Instagram,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import krebraLogo from "@/assets/krebra-logo-dark.png";

const navItems = {
  creator: [
    { title: "Dashboard", url: "/creator/dashboard", icon: LayoutDashboard },
    { title: "Campaigns", url: "/creator/campaigns", icon: Megaphone },
    { title: "Applications", url: "/creator/applications", icon: FileText },
    { title: "Submissions", url: "/creator/submissions", icon: Send },
    { title: "Earnings", url: "/creator/earnings", icon: DollarSign },
    { title: "Payouts", url: "/creator/payouts", icon: Wallet },
    { title: "Instagram", url: "/creator/instagram", icon: Instagram },
    { title: "Profile", url: "/creator/profile", icon: User },
    { title: "Settings", url: "/creator/settings", icon: Settings },
  ],
  brand: [
    { title: "Dashboard", url: "/brand/dashboard", icon: LayoutDashboard },
    { title: "Campaigns", url: "/brand/campaigns", icon: Megaphone },
    { title: "Applications", url: "/brand/applications", icon: FileText },
    { title: "Submissions", url: "/brand/submissions", icon: Send },
    { title: "Budget", url: "/brand/budget", icon: DollarSign },
    { title: "Profile", url: "/brand/profile", icon: User },
    { title: "Settings", url: "/brand/settings", icon: Settings },
  ],
  admin: [
    { title: "Dashboard", url: "/admin", icon: LayoutDashboard },
    { title: "Users", url: "/admin/users", icon: Users },
    { title: "Creators", url: "/admin/creators", icon: User },
    { title: "Brands", url: "/admin/brands", icon: Building2 },
    { title: "Campaigns", url: "/admin/campaigns", icon: Megaphone },
    { title: "Applications", url: "/admin/applications", icon: FileText },
    { title: "Submissions", url: "/admin/submissions", icon: Send },
    { title: "Payouts", url: "/admin/payouts", icon: Wallet },
    { title: "Flags", url: "/admin/flags", icon: Flag },
    { title: "Settings", url: "/admin/settings", icon: Settings },
  ],
};

function AppSidebar({ role }: { role: "creator" | "brand" | "admin" }) {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const items = navItems[role];
  const location = useLocation();
  const roleLabel = role.charAt(0).toUpperCase() + role.slice(1);
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  return (
    <Sidebar collapsible="icon" className="border-r-0">
      <div className="flex items-center h-[60px] px-4 border-b border-sidebar-border">
        {!collapsed ? (
          <Link to="/" className="flex items-center">
            <img src={krebraLogo} alt="Krebra" className="h-7" />
          </Link>
        ) : (
          <Link to="/" className="mx-auto">
            <img src={krebraLogo} alt="Krebra" className="h-7 w-7 object-contain object-left" />
          </Link>
        )}
      </div>
      <SidebarContent className="px-2 pt-4">
        <SidebarGroup>
          <SidebarGroupLabel className="text-[11px] font-medium text-sidebar-foreground/40 uppercase tracking-widest px-3 mb-1">{roleLabel}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => {
                const active = location.pathname === item.url || (item.url !== "/" && location.pathname.startsWith(item.url + "/"));
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={active}
                      className={`rounded-lg h-9 transition-all duration-150 ${active ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium" : "text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"}`}
                    >
                      <NavLink to={item.url} end>
                        <item.icon className="h-[18px] w-[18px]" />
                        {!collapsed && <span className="text-[13px]">{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <div className="mt-auto px-2 pb-4 border-t border-sidebar-border pt-3">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleLogout}
              className="rounded-lg h-9 text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground transition-colors"
            >
              <LogOut className="h-[18px] w-[18px]" />
              {!collapsed && <span className="text-[13px]">Log Out</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </div>
    </Sidebar>
  );
}

export default function DashboardLayout({ role }: { role: "creator" | "brand" | "admin" }) {
  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar role={role} />
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-[60px] flex items-center justify-between border-b border-border bg-card px-5 lg:px-8 sticky top-0 z-30">
            <div className="flex items-center gap-3">
              <SidebarTrigger className="h-8 w-8 text-muted-foreground hover:text-foreground" />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="relative h-9 w-9 rounded-lg text-muted-foreground hover:text-foreground">
                <Bell className="h-[18px] w-[18px]" />
                <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-destructive" />
              </Button>
              <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <User className="h-4 w-4 text-primary" />
              </div>
            </div>
          </header>
          <main className="flex-1 p-5 lg:p-8 overflow-auto bg-background">
            <div className="page-container">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
